import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { api } from "../lib/api";
import type { ManagedQueue } from "../lib/api";
import { FormField, TextInput } from "../components/primitives/FormField";
import { Button } from "../components/primitives/Button";
import { SchemaForm } from "../components/SchemaForm";
import type { JsonSchema, FormValue } from "../lib/schema-form/types";
import { secretSchemaHasFields } from "../lib/schema-form/walk";
import { PageHelp } from "../components/PageHelp";

interface Connector {
  connectorType: string;
  label: string;
  configJsonSchema: JsonSchema | null;
  secretJsonSchema: JsonSchema | null;
  documentation?: { summary?: string } | null;
}

interface Organization { id: string; name: string }


/**
 * Create or edit a connection, with the fields driven by the connector's own
 * config schema so a new connector needs no change here.
 *
 * Credentials are write-only: an existing secret is never read back into the
 * form, so leaving a field untouched keeps what is stored rather than clearing
 * it.
 */
export function ConnectionEditor() {
  const nav = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const editing = Boolean(id);

  const [connectors, setConnectors] = useState<Connector[] | null>(null);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [connectorType, setConnectorType] = useState("");
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState<FormValue>({});
  const [secretValue, setSecretValue] = useState<FormValue>({});
  const [showRotate, setShowRotate] = useState(false);
  const [rotateValue, setRotateValue] = useState<FormValue>({});
  const [error, setError] = useState<string | null>(null);
  const [queues, setQueues] = useState<ManagedQueue[]>([]);

  useEffect(() => {
    if (connectorType === "custom-sqs" && organizationId) {
      api.listQueues(organizationId).then((r) => setQueues(r.queues)).catch(() => setQueues([]));
    } else {
      setQueues([]);
    }
  }, [connectorType, organizationId]);

  useEffect(() => {
    const safe = <T,>(p: Promise<T>): Promise<T | null> =>
      p.catch((e) => { setError(e instanceof Error ? e.message : String(e)); return null; });
    safe(api.listConnectors()).then((r) => setConnectors((r?.connectors as Connector[]) ?? []));
    safe(api.listOrganizations()).then((r) => setOrgs((r?.organizations as Organization[]) ?? []));
  }, []);

  useEffect(() => {
    if (!editing) return;
    // Reset rotation + secret form state on connection change so an unsaved
    // form on connection A can't render (or POST) under connection B's id.
    setShowRotate(false);
    setRotateValue({});
    setSecretValue({});
    api.getConnectorInstance(id!).then((r) => {
      setOrganizationId(r.instance.organizationId ?? "");
      setConnectorType(r.instance.connectorType ?? "");
      setName(r.instance.name ?? "");
      setEnabled(Boolean(r.instance.enabled));
      setConfig((r.instance.config as FormValue) ?? {});
    }).catch((err) => setError(err.message ?? String(err)));
  }, [id, editing]);

  const selected = connectors?.find((c) => c.connectorType === connectorType);

  /** Save the connection — update in place when editing, otherwise create — and
   *  return to the list. Credentials are only sent when the operator filled some in. */
  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing) {
        await api.updateConnectorInstance(id!, { name, enabled, config });
      } else {
        // On create, include the secret value only when the operator has
        // actually filled in at least one field. An empty {} would fail the
        // server-side shape validation and blocks the "create now, set
        // credentials later" path.
        const body: Record<string, unknown> = { organizationId, connectorType, name, enabled, config };
        if (secretSchemaHasFields(selected?.secretJsonSchema ?? null) && Object.keys(secretValue).length > 0) {
          body.secret = secretValue;
        }
        await api.createConnectorInstance(body);
      }
      nav("/connections");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Replace the stored credentials with the newly entered ones. */
  async function onRotate() {
    setError(null);
    try {
      await api.rotateConnectorInstanceSecret(id!, rotateValue);
      setShowRotate(false);
      setRotateValue({});
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (connectors === null || orgs === null) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div className="uppercase-label" style={{ marginBottom: 16 }}>NEW CONNECTION</div>
        <PageHelp />
        {error ? (
          <p className="page-help">Couldn't load editor data: {error}</p>
        ) : (
          <p className="page-help">Loading…</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 720 }}>
      <div className="uppercase-label" style={{ marginBottom: 16 }}>
        {editing ? "EDIT CONNECTION" : "NEW CONNECTION"}
      </div>
      <PageHelp />

      {!editing && (
        <FormField label="CLIENT *">
          <select
            className="field__input"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            required
          >
            <option value="">—</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </FormField>
      )}

      {!editing && (
        <FormField
          label="CONNECTOR TYPE *"
          hint={selected?.documentation?.summary}
        >
          <select
            className="field__input"
            value={connectorType}
            onChange={(e) => {
              setConnectorType(e.target.value);
              setConfig({});
              setSecretValue({});
            }}
            required
          >
            <option value="">—</option>
            {connectors.map((c) => (
              <option key={c.connectorType} value={c.connectorType}>{c.label}</option>
            ))}
          </select>
        </FormField>
      )}

      <FormField label="NAME *" hint="An operator-readable identifier, e.g. denver-prod">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
      </FormField>

      <FormField label="ENABLED">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
      </FormField>

      {selected?.connectorType === "custom-sqs" && queues.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>MANAGED QUEUE</div>
          <select
            className="field__input"
            value={(config as { queueUrl?: string }).queueUrl ?? ""}
            onChange={(e) => {
              const q = queues.find((x) => x.queueUrl === e.target.value);
              if (q) setConfig({ ...config, queueUrl: q.queueUrl, region: q.region, fifo: q.fifo });
            }}
          >
            <option value="">Select a managed queue…</option>
            {queues.map((q) => (
              <option key={q.id} value={q.queueUrl}>
                {q.name} ({q.fifo ? "FIFO" : "STANDARD"})
              </option>
            ))}
          </select>
        </div>
      )}

      {selected?.configJsonSchema && (
        <div style={{ marginTop: 16 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>CONFIG</div>
          <SchemaForm schema={selected.configJsonSchema} value={config} onChange={setConfig} />
        </div>
      )}

      {/* On CREATE: inline secret form. The API derives the secretName from
          the new instance id, so the operator never types it. */}
      {!editing && selected && secretSchemaHasFields(selected.secretJsonSchema) && (
        <div style={{ marginTop: 16 }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>SECRET</div>
          <SchemaForm
            schema={selected.secretJsonSchema}
            value={secretValue}
            onChange={setSecretValue}
            maskSecrets
          />
        </div>
      )}

      {error && <div className="field__error" style={{ marginBottom: 12 }}>{error}</div>}

      <Button type="submit" variant="primary">{editing ? "SAVE" : "CREATE"}</Button>

      {/* On EDIT: secret rotation lives behind an explicit action — keeps the
          intent loud, prevents accidental overwrites on save. */}
      {editing && selected && secretSchemaHasFields(selected.secretJsonSchema) && (
        <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
          <div className="uppercase-label" style={{ marginBottom: 8 }}>CREDENTIALS</div>
          {!showRotate ? (
            <Button type="button" variant="secondary" onClick={() => setShowRotate(true)}>
              ROTATE CREDENTIALS
            </Button>
          ) : (
            <div style={{ border: "1px solid var(--color-border)", padding: 16, marginTop: 8 }}>
              <SchemaForm
                schema={selected.secretJsonSchema}
                value={rotateValue}
                onChange={setRotateValue}
                maskSecrets
              />
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <Button type="button" variant="primary" onClick={onRotate}>SAVE NEW CREDENTIALS</Button>
                <Button type="button" variant="ghost" onClick={() => { setShowRotate(false); setRotateValue({}); }}>
                  CANCEL
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </form>
  );
}
